import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  LEVELS,
  SupportLevel,
  agreementFor,
  formatTarget,
} from '@app/components/pages/organizations/organization/clientReporting/product/sources/incidents';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Textarea } from '@ui/components/ui/textarea';

import { cardClass } from '@app/constants/styles';
import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { capitalize } from '@app/utils';
import { ClientAuditTabValue, ProductEntity, ProductStatus } from '@data';
import { useListClientCompanies } from '@data/hooks/clientCompanies';
import { useUpdateProductDetail } from '@data/hooks/products';

import { toProductUrl } from '../productUrl';

// A value the picker can hold, since a Select cannot carry an empty string as an option.
const NO_AGREEMENT = 'none';

const NO_CLIENT = 'none';
const STATUSES = [ProductStatus.active, ProductStatus.support, ProductStatus.archived, ProductStatus.deactivated];

// In the order the audit tab lists them.
const AUDITS: { value: ClientAuditTabValue; labelKey: string }[] = [
  { value: 'quality', labelKey: 'tabs.qualityAudit' },
  { value: 'security', labelKey: 'tabs.securityAudit' },
  { value: 'productivity', labelKey: 'tabs.productivity' },
];
const ALL_AUDITS = AUDITS.map(audit => audit.value);

const viewLabel = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
export const pillClass = 'rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground';
const dash = <span className="text-muted-foreground">—</span>;

/** One aligned label/value row, matching the member detail page. */
const DetailRow = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <div className="flex flex-col gap-1 py-4 first:pt-0 last:pb-0 sm:flex-row sm:gap-6">
    <dt className={`${viewLabel} sm:w-44 sm:shrink-0 sm:pt-1.5`}>{label}</dt>
    <dd className="min-w-0 flex-1 text-sm text-foreground">{children}</dd>
  </div>
);

/** The audits the client is shown, by name; none says so rather than leaving the row blank. */
const ClientAuditsView = ({ shown }: { shown: ClientAuditTabValue[] }): ReactElement => {
  const { t } = useTranslation();
  if (!shown.length) return <span className="text-muted-foreground">{t('productDetail.clientAuditsNone')}</span>;
  return (
    <>
      {AUDITS.filter(audit => shown.includes(audit.value))
        .map(audit => t(audit.labelKey))
        .join(', ')}
    </>
  );
};

type FormValues = {
  name: string;
  projectCode: string;
  clientCompanyId: string;
  description: string;
  url: string;
  status: ProductStatus;
  billable: string;
  /** '' is a product deliberately not under support, which is a different answer from any level. */
  supportLevel: SupportLevel | '';
  /** '' is off: a sent report waits for the client however long they take. */
  autoApproveAfterDays: string;
  clientAuditTabs: ClientAuditTabValue[];
};

/** The product's own fields, read-first and saved in one request. Its team is on Team and rates. */
function ProductDetailsCard({
  product,
  organizationId,
  canEdit,
}: {
  product: ProductEntity;
  organizationId: string;
  canEdit: boolean;
}): ReactElement {
  const { t } = useTranslation();

  // The client COMPANY, which is what the read-only view below already shows. Editing used to offer
  // client people instead, so the field you read and the field you changed were different things.
  // Only the edit form picks a company, and the list is ours: a client reads the name off the product.
  const { data: clientCompanies } = useListClientCompanies(canEdit ? organizationId : undefined);
  // Only people who still work here are offered, which is also what the API accepts.

  const save = useUpdateProductDetail(product.id);

  // The card reads rather than edits until asked, the same as a person's page: a form standing open
  // invites changes nobody came to make.
  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      projectCode: '',
      clientCompanyId: NO_CLIENT,
      description: '',
      url: '',
      status: ProductStatus.active,
      billable: 'yes',
      supportLevel: '',
      autoApproveAfterDays: '',
      clientAuditTabs: ALL_AUDITS,
    },
  });

  const formValuesFor = (p: ProductEntity | undefined): FormValues => ({
    name: p?.name ?? '',
    projectCode: p?.projectCode ?? '',
    clientCompanyId: p?.clientCompanyId ?? NO_CLIENT,
    description: p?.description ?? '',
    url: p?.url ?? '',
    status: p?.status ?? ProductStatus.active,
    billable: p?.billable === false ? 'no' : 'yes',
    supportLevel: (p?.supportLevel as SupportLevel | undefined) ?? '',
    autoApproveAfterDays: p?.autoApproveAfterDays ? String(p.autoApproveAfterDays) : '',
    clientAuditTabs: p?.clientAuditTabs ?? ALL_AUDITS,
  });

  useEffect(() => {
    if (!product) return;
    reset(formValuesFor(product));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, reset]);

  const clientItems = selectItemsByValue([
    { value: NO_CLIENT, label: t('productDetail.noClient') },
    ...(clientCompanies?.items ?? [])
      .map(company => ({ value: company.id, label: company.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]);
  const statusItems = selectItemsByValue(
    STATUSES.map(status => ({ value: status, label: capitalize(status) ?? status })),
  );
  const billableItems = selectItemsByValue([
    { value: 'yes', label: t('yes') },
    { value: 'no', label: t('no') },
  ]);
  const supportItems = selectItemsByValue([
    { value: NO_AGREEMENT, label: t('incidents.noSupportAgreement') },
    ...LEVELS.map(level => ({ value: level, label: t(`incidents.level.${level}`) })),
  ]);

  /** The window, and the priorities covered whatever the hour: without the second half a Gold P1 at
      23:00 reads as uncovered until morning. */
  const agreementLine = (level: SupportLevel): string => {
    const agreement = agreementFor(level);

    return agreement.aroundTheClock.length
      ? `${agreement.window} · ${t('incidents.aroundTheClockPriorities', { priorities: agreement.aroundTheClock.join(', ') })}`
      : agreement.window;
  };

  // What each priority is promised, answer then fix, on one line: four rows of it would push the card's
  // other fields off the screen to say something nobody reads twice.
  const targetsLine = (level: SupportLevel): string =>
    agreementFor(level)
      .targets.map(
        target => `${target.priority} ${formatTarget(target.responseHours)} / ${formatTarget(target.resolutionHours)}`,
      )
      .join(' · ');

  /* Read side of the agreement row. A level shows what it promises under it; no level says so plainly,
     since an empty row reads as a field nobody has got to rather than a product deliberately not on one. */
  const supportView = product?.supportLevel ? (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-foreground">{t(`incidents.level.${product.supportLevel}`)}</span>
      <span className="text-[13px] text-muted-foreground">{agreementLine(product.supportLevel as SupportLevel)}</span>
      <span className="text-[13px] text-muted-foreground">
        {t('incidents.agreementTargets', { targets: targetsLine(product.supportLevel as SupportLevel) })}
      </span>
    </div>
  ) : (
    <span className="text-muted-foreground">{t('incidents.noSupportAgreement')}</span>
  );

  const stopEditing = (): void => {
    reset(formValuesFor(product));
    setFailed(null);
    setEditing(false);
  };

  const onSubmit = (values: FormValues): void => {
    setFailed(null);
    save.mutate(
      {
        name: values.name.trim(),
        projectCode: values.projectCode.trim() || null,
        clientCompanyId: values.clientCompanyId === NO_CLIENT ? null : values.clientCompanyId,
        description: values.description,
        url: toProductUrl(values.url),
        status: values.status,
        billable: values.billable === 'yes',
        // Taking the agreement away stops anything new being logged against the product and hides its
        // standing, but leaves the incidents already on it alone — they still happened.
        supportLevel: values.supportLevel || null,
        autoApproveAfterDays: values.autoApproveAfterDays.trim() ? Number(values.autoApproveAfterDays) : null,
        clientAuditTabs: values.clientAuditTabs,
      },
      {
        onSuccess: () => setEditing(false),
        // Kept open with what was typed still in it, because a save that failed has nothing to show yet.
        onError: error => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          setFailed(status === 400 ? t('productDetail.saveRejected') : t('productDetail.saveFailed'));
        },
      },
    );
  };

  const { isPending } = save;

  const isMobile = useIsMobile();
  // A phone edits in a sheet (below), leaving the card as it reads.
  const inline = editing && !isMobile;

  const cardActions = (() => {
    if (!canEdit) return null;
    if (!inline) {
      return (
        <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="size-4" />
          {t('edit')}
        </Button>
      );
    }
    return (
      <div className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={isPending} onClick={stopEditing}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={isPending || !isDirty}>
          {t('actions.save')}
        </Button>
      </div>
    );
  })();

  const rows = (edit: boolean): React.ReactElement => (
    <dl className="divide-y divide-border">
      <DetailRow label={t('titles.name')}>
        {edit ? (
          <Controller
            name="name"
            control={control}
            rules={{ required: t('productDetail.required') }}
            render={({ field }) => (
              <div className="max-w-[22rem]">
                <Input
                  aria-label={t('titles.name')}
                  {...field}
                  disabled={isPending}
                  autoComplete="off"
                  className="bg-popover"
                />
                {errors.name?.message ? <p className="mt-1 text-sm text-destructive">{errors.name.message}</p> : null}
              </div>
            )}
          />
        ) : (
          <span className="font-semibold text-foreground">{capitalize(product.name)}</span>
        )}
      </DetailRow>

      <DetailRow label={t('projectCode')}>
        {edit ? (
          <Controller
            name="projectCode"
            control={control}
            render={({ field }) => (
              <Input
                aria-label={t('projectCode')}
                {...field}
                disabled={isPending}
                autoComplete="off"
                className="max-w-[12rem] bg-popover"
              />
            )}
          />
        ) : (
          <span className="font-mono text-sm font-medium text-foreground">{product.projectCode || dash}</span>
        )}
      </DetailRow>

      <DetailRow label={t('client')}>
        {edit ? (
          <Controller
            name="clientCompanyId"
            control={control}
            render={({ field }) => (
              <Select items={clientItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
                <SelectTrigger aria-label={t('client')} className="w-56 max-w-full">
                  <SelectValue placeholder={t('productDetail.noClient')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CLIENT}>{t('productDetail.noClient')}</SelectItem>
                  {(clientCompanies?.items ?? [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          /*
           * The company first, which is what "client" means to a reader (architecture Decision 1) and
           * what the Add client flow links a new product to. The client person behind it is the older,
           * narrower link and stands in when no company is set, so a product from before epic 12 still
           * says who it belongs to instead of nothing.
           */
          product.clientCompanyName || product.clientName || dash
        )}
      </DetailRow>

      <DetailRow label={t('titles.productDescription')}>
        {edit ? (
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                aria-label={t('titles.productDescription')}
                rows={3}
                {...field}
                disabled={isPending}
                className="bg-popover"
              />
            )}
          />
        ) : (
          product.description || dash
        )}
      </DetailRow>

      <DetailRow label={t('productDetail.address')}>
        {/* eslint-disable-next-line no-nested-ternary -- read state has two forms: a link, or nothing recorded */}
        {edit ? (
          <Controller
            name="url"
            control={control}
            render={({ field }) => (
              <div className="max-w-[22rem]">
                <Input
                  aria-label={t('productDetail.address')}
                  {...field}
                  disabled={isPending}
                  autoComplete="off"
                  className="bg-popover"
                  aria-describedby="product-url-hint"
                />
                <p id="product-url-hint" className="mt-1 text-xs text-muted-foreground">
                  {t('productDetail.addressHint')}
                </p>
              </div>
            )}
          />
        ) : product.url ? (
          <a
            href={product.url}
            target="_blank"
            rel="noreferrer"
            className="text-link underline-offset-4 hover:underline"
          >
            {product.url}
          </a>
        ) : (
          dash
        )}
      </DetailRow>

      <DetailRow label={t('status')}>
        {edit ? (
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select items={statusItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
                <SelectTrigger aria-label={t('status')} className="w-56 max-w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(status => (
                    <SelectItem key={status} value={status}>
                      {capitalize(status)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          capitalize(product.status)
        )}
      </DetailRow>

      <DetailRow label={t('billableWork')}>
        {edit ? (
          <Controller
            name="billable"
            control={control}
            render={({ field }) => (
              <Select items={billableItems} value={field.value} onValueChange={field.onChange} disabled={isPending}>
                <SelectTrigger aria-label={t('billableWork')} className="w-32 max-w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t('yes')}</SelectItem>
                  <SelectItem value="no">{t('no')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          <span className="font-semibold text-foreground">{product.billable === false ? t('no') : t('yes')}</span>
        )}
      </DetailRow>

      {/* The level is the whole choice: the service window and every target follow from it, so they
          are shown beside it rather than asked for. */}
      <DetailRow label={t('incidents.supportAgreement')}>
        {edit ? (
          <Controller
            name="supportLevel"
            control={control}
            render={({ field }) => (
              <div className="flex max-w-sm flex-col gap-1">
                <Select
                  items={supportItems}
                  value={field.value || NO_AGREEMENT}
                  onValueChange={next => field.onChange(next === NO_AGREEMENT ? '' : next)}
                  disabled={isPending}
                >
                  <SelectTrigger aria-label={t('incidents.supportAgreement')} className="w-48 max-w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_AGREEMENT}>{t('incidents.noSupportAgreement')}</SelectItem>
                    {LEVELS.map(level => (
                      <SelectItem key={level} value={level}>
                        {t(`incidents.level.${level}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {field.value
                    ? `${agreementLine(field.value as SupportLevel)} · ${t('incidents.agreementTargets', { targets: targetsLine(field.value as SupportLevel) })}`
                    : t('incidents.noSupportAgreementHint')}
                </p>
              </div>
            )}
          />
        ) : (
          supportView
        )}
      </DetailRow>

      <DetailRow label={t('productDetail.autoApprove')}>
        {/* eslint-disable-next-line no-nested-ternary -- read state has two forms: a term, or off */}
        {edit ? (
          <Controller
            name="autoApproveAfterDays"
            control={control}
            rules={{
              validate: value =>
                !value.trim() || (/^\d+$/.test(value.trim()) && Number(value) >= 1 && Number(value) <= 365)
                  ? true
                  : t('productDetail.autoApproveOutOfRange'),
            }}
            render={({ field }) => (
              <div className="flex max-w-sm flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={t('productDetail.autoApprove')}
                    {...field}
                    inputMode="numeric"
                    disabled={isPending}
                    autoComplete="off"
                    className="w-20 bg-popover"
                    aria-describedby="product-auto-approve-hint"
                  />
                  <span className="text-sm text-muted-foreground">{t('productDetail.autoApproveDays')}</span>
                </div>
                {errors.autoApproveAfterDays?.message ? (
                  <p className="text-sm text-destructive">{errors.autoApproveAfterDays.message}</p>
                ) : (
                  <p id="product-auto-approve-hint" className="text-[11px] text-muted-foreground">
                    {t('productDetail.autoApproveHint')}
                  </p>
                )}
              </div>
            )}
          />
        ) : product.autoApproveAfterDays ? (
          <span className="font-semibold text-foreground">
            {t('productDetail.autoApproveAfter', { count: product.autoApproveAfterDays })}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('productDetail.autoApproveOff')}</span>
        )}
      </DetailRow>
      {/* Ours alone: a client reads the audits they are shown, not the list of what is held back. */}
      {canEdit && (
        <DetailRow label={t('productDetail.clientAudits')}>
          {edit ? (
            <Controller
              name="clientAuditTabs"
              control={control}
              render={({ field }) => (
                <div className="flex max-w-sm flex-col gap-1">
                  <MultiSelectField
                    label={<span className="sr-only">{t('productDetail.clientAudits')}</span>}
                    options={AUDITS.map(audit => ({ value: audit.value, label: t(audit.labelKey) }))}
                    value={field.value}
                    // Kept in the tab's own order, whichever order they were ticked in.
                    onChange={values => field.onChange(ALL_AUDITS.filter(each => values.includes(each)))}
                    emptyLabel={t('productDetail.clientAuditsNone')}
                    searchable={false}
                    clearable={false}
                    summaryThreshold={1}
                    renderCountSummary={count =>
                      count === ALL_AUDITS.length
                        ? t('productDetail.clientAuditsAll')
                        : t('productDetail.clientAuditsCount', { count })
                    }
                    disabled={isPending}
                    className="w-56 max-w-full"
                  />
                  <p className="text-[11px] text-muted-foreground">{t('productDetail.clientAuditsHint')}</p>
                </div>
              )}
            />
          ) : (
            <ClientAuditsView shown={product.clientAuditTabs ?? ALL_AUDITS} />
          )}
        </DetailRow>
      )}
    </dl>
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={`${cardClass} mb-6`}>
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="text-lg font-bold text-foreground">{t('productDetails')}</h2>
          {cardActions}
        </div>

        {rows(inline)}

        {inline && failed ? <p className="mt-4 text-sm text-destructive">{failed}</p> : null}
      </form>
      <EditSheet
        open={editing && isMobile}
        title={t('productDetails')}
        onCancel={stopEditing}
        onSubmit={handleSubmit(onSubmit)}
        isPending={isPending}
        canSave={isDirty}
        error={failed}
      >
        {rows(true)}
      </EditSheet>
    </>
  );
}

export default ProductDetailsCard;
